
import React from 'react';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandLoading } from '@/components/ui/command';
import { PopoverContent } from '@/components/ui/popover';
import { LoaderCircleIcon } from 'lucide-react';
import { LocationOption } from '@/types/weather';
import LocationItem from './LocationItem';
import { useIsMobile } from '@/hooks/use-mobile';

interface LocationDropdownProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  locations: LocationOption[];
  favorites: LocationOption[];
  selectedLocation?: LocationOption;
  isLoading: boolean;
  onSelectLocation: (location: LocationOption) => void;
  onToggleFavorite: (location: LocationOption) => void;
  isFavorite: (location: LocationOption) => boolean;
  onClose: () => void;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  searchTerm,
  onSearchChange,
  locations,
  favorites,
  selectedLocation,
  isLoading,
  onSelectLocation,
  onToggleFavorite,
  isFavorite,
  onClose
}) => {
  const isMobile = useIsMobile();
  
  const handleSelect = (location: LocationOption) => {
    onSelectLocation(location);
    onClose();
  };
  
  return (
    <PopoverContent className="p-0 w-[300px]" sideOffset={5} align="start">
      <Command className="rounded-lg border shadow-md">
        <div className="sticky top-0 z-10 bg-background rounded-t-lg">
          <CommandInput 
            placeholder="Search for a location..." 
            value={searchTerm}
            onValueChange={onSearchChange}
            className="py-3"
          />
        </div>
        <CommandList className={isMobile ? "max-h-[200px]" : "max-h-[300px]"}>
          {isLoading && (
            <CommandLoading>
              <div className="flex items-center justify-center p-4">
                <LoaderCircleIcon className="h-4 w-4 mr-2 animate-spin" />
                <span>Searching...</span>
              </div>
            </CommandLoading>
          )}
          
          <CommandEmpty>No locations found</CommandEmpty>

          {favorites.length > 0 && (
            <CommandGroup heading="Favorites">
              {favorites.map((location) => (
                <LocationItem 
                  key={`fav-${location.id}`}
                  location={location} 
                  isSelected={selectedLocation?.id === location.id}
                  isFavorite={true}
                  onSelect={handleSelect}
                  onToggleFavorite={onToggleFavorite}
                />
              ))}
            </CommandGroup>
          )}

          <CommandGroup heading={searchTerm ? "Results" : "Popular Italian Cities"}>
            {locations.map((location) => (
              <LocationItem 
                key={location.id}
                location={location} 
                isSelected={selectedLocation?.id === location.id}
                isFavorite={isFavorite(location)}
                onSelect={handleSelect}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );
};

export default LocationDropdown;
